import { c as _c } from "react-compiler-runtime";
import { useSelect } from "downshift";
import { Check, ChevronDown } from "lucide-react";
import React, { forwardRef, useEffect, useState } from "react";
import { Text } from "@/components/primitives";
import { cn } from "@/core/utils";
export type SelectProps = {
  readonly "options": SelectOption[];
  readonly "onSelect": (selected: SelectOption | null) => void;
  readonly "placeholder"?: string;
  readonly "value"?: SelectOption;
  readonly "direction"?: "up" | "down";
  readonly "aria-invalid"?: boolean;
  readonly "aria-describedby": string;
  readonly "id": string;
  readonly "disabled"?: boolean;
  readonly "showAddButton"?: boolean;
  readonly "addButtonText"?: string;
  readonly "onAddNewOption"?: () => void;
  readonly "className"?: string;
  readonly "variant"?: "default" | "v3";
};
export type SelectOption<T = unknown> = {
  label: string;
  value: T;
  disabled?: boolean;
  supportingText?: string;
} & Partial<{
  [key: string]: unknown;
}>;
const Select = forwardRef<HTMLDivElement, SelectProps>(function Select(t0, ref) {
  const $ = _c(31);
  const {
    options,
    onSelect,
    placeholder,
    value,
    direction: t1,
    id,
    disabled: t2,
    showAddButton: t3,
    addButtonText: t4,
    onAddNewOption,
    className,
    variant: t5,
    ...props
  } = t0;
  const direction = t1 === undefined ? "down" : t1;
  const disabled = t2 === undefined ? false : t2;
  const showAddButton = t3 === undefined ? false : t3;
  const addButtonText = t4 === undefined ? "Add New" : t4;
  const variant = t5 === undefined ? "default" : t5;
  const [selectedItem, setSelectedItem] = useState(value);
  let t6;
  let t7;
  if ($[0] !== value) {
    t6 = () => {
      setSelectedItem(value);
    };
    t7 = [value];
    $[0] = value;
    $[1] = t6;
    $[2] = t7;
  } else {
    t6 = $[1];
    t7 = $[2];
  }
  useEffect(t6, t7);
  let t8;
  if ($[3] !== onSelect) {
    t8 = t9 => {
      const {
        selectedItem: newSelectedItem
      } = t9;
      setSelectedItem(newSelectedItem);
      onSelect(newSelectedItem);
    };
    $[3] = onSelect;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  let t9;
  if ($[5] !== options || $[6] !== t8) {
    t9 = {
      items: options,
      itemToString: _temp,
      onSelectedItemChange: t8
    };
    $[5] = options;
    $[6] = t8;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex
  } = useSelect(t9);
  const getSelectBackgroundClassName = _temp2;
  const renderHeader = () => <div className={cn("relative", "rounded-xs")} data-testid="Select_header"><input {...getToggleButtonProps()} aria-describedby={props["aria-describedby"]} aria-invalid={props["aria-invalid"]} className={cn("peer block w-full flex-1 cursor-pointer appearance-none px-3", "text-sm transition-colors ease-in-out", "-ring-offset-1 ring-light-grey border-none ring-1", "hover:ring-gray-400", "aria-invalid:ring-red aria-invalid:placeholder:text-red aria-invalid:ring-1", "focus:placeholder:text-secondary! focus:ring-1 focus:ring-blue-400! focus:outline-none", disabled && "cursor-not-allowed opacity-50", variant === "v3" ? "h-[31px] rounded-full" : "h-10 rounded bg-white", className, getSelectBackgroundClassName(selectedItem))} disabled={disabled} id={id} placeholder={placeholder || " "} readOnly={true} value={selectedItem?.label ?? ""} /><div className={cn("pointer-events-none absolute top-1/2 right-0 flex -translate-y-1/2 cursor-pointer items-center justify-center pr-3")}><ChevronDown aria-label={isOpen ? "Close menu" : "Open menu"} className={cn(isOpen ? "rotate-180" : "rotate-0", "h-6 w-6 transform transition-all duration-200 ease-in-out", props["aria-invalid"] && "text-red-600")} data-testid="dropdown-toggle-arrow" /></div></div>;
  let t10;
  if ($[8] !== addButtonText || $[9] !== direction || $[10] !== getItemProps || $[11] !== highlightedIndex || $[12] !== onAddNewOption || $[13] !== options || $[14] !== selectedItem?.value || $[15] !== showAddButton) {
    t10 = () => <ul className={cn("absolute right-0 left-0 z-20 max-h-[150px] overflow-y-auto rounded border border-gray-300 bg-white shadow-sm", direction === "up" ? "top-[-5px] bottom-auto -translate-y-full" : "bottom-[-5px] translate-y-full")}>{options.map((item_0, index) => {
        const isSelected = selectedItem?.value === item_0.value;
        const isHighlighted = highlightedIndex === index;
        const isDisabled = item_0.disabled;
        return <li className={cn("text-body1 flex h-10 cursor-pointer items-center justify-between p-2 transition-colors", isSelected && "bg-gray/50", isHighlighted && !isSelected && "bg-gray", isDisabled && "cursor-not-allowed opacity-50")} key={`${item_0.value}${index}`} {...getItemProps({
          item: item_0,
          index,
          disabled: item_0.disabled
        })}>{item_0.label}{item_0.supportingText && <Text component="p" text={item_0.supportingText} tone="muted" variant="caption" />}{isSelected && <Check className="h-4 w-4" />}</li>;
      })}{showAddButton && onAddNewOption && <li className="mt-2 border-t border-gray-200 pt-2"><button className="flex h-10 w-full cursor-pointer items-center justify-center rounded px-2 text-sm text-[#068762] transition-colors hover:bg-[#068762]/80 hover:text-white" type="button" onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onAddNewOption();
        }}>+ {addButtonText}</button></li>}</ul>;
    $[8] = addButtonText;
    $[9] = direction;
    $[10] = getItemProps;
    $[11] = highlightedIndex;
    $[12] = onAddNewOption;
    $[13] = options;
    $[14] = selectedItem?.value;
    $[15] = showAddButton;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const renderOptionsList = t10;
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn("Select relative h-10 w-full");
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const t12 = "Select";
  const t13 = renderHeader();
  let t14;
  if ($[18] !== getMenuProps) {
    t14 = getMenuProps();
    $[18] = getMenuProps;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== isOpen || $[21] !== renderOptionsList) {
    t15 = isOpen && renderOptionsList();
    $[20] = isOpen;
    $[21] = renderOptionsList;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t14 || $[24] !== t15) {
    t16 = <div {...t14} className="rounded">{t15}</div>;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== ref || $[27] !== t11 || $[28] !== t13 || $[29] !== t16) {
    t17 = <div className={t11} data-testid={t12} ref={ref}>{t13}{t16}</div>;
    $[26] = ref;
    $[27] = t11;
    $[28] = t13;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  return t17;
});
export default Select;
export function getSelectOptionForKey<T>(key: string, options: SelectOption<T>[]): SelectOption<T> | undefined {
  return options.find(option => option.value === key);
}
function _temp(item) {
  return item ? item.label : "";
}
function _temp2(_item) {
  return "";
}